import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Eye, FileSignature, MoreHorizontal, RefreshCw } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { formatDay, todayIso } from "../lib/finance";
import {
  ACTION_LABELS, HISTORY_LABELS, ORGANISATION_ROLES, PARTY_KIND_LABELS, SIGNATURE_MODES, datesHint, primaryAction, recordsError,
} from "../lib/records";
import { ClassificationBadge, Field, FileLinks, StateBadge } from "../components/records/RecordBits";
import { usePeople } from "../components/records/RecordFields";
import { AccessDialog, ConfirmDialog, EditDialog, ReasonDialog, SignDialog, TerminateDialog, VersionDialog } from "../components/records/RecordDialogs";
import { Button } from "../components/ui/button";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { DocumentObligationsCard } from "../components/records/ObligationBits";
import { Skeleton } from "../components/ui/skeleton";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuSeparator as DropdownMenuSeparator, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

const when = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

function historyDetail(entry) {
  const d = entry.data || {};
  if (d.reason) return `Motif : ${d.reason}`;
  if (entry.kind === "sign") {
    const people = [...(d.jamora_signatories || []), ...(d.counterpart_signatories || [])].join(", ");
    return `Signé le ${formatDay(d.signed_on)}${people ? ` par ${people}` : ""}`;
  }
  if (entry.kind === "terminate" && d.terminated_on) return `Fin le ${formatDay(d.terminated_on)}`;
  return "";
}

function Downloads({ doc }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    setOpen(true);
    try {
      const { data } = await api.get(`/records/documents/${doc.id}/downloads`);
      setRows(data.items);
    } catch {
      setRows([]);
    }
  };
  if (!open) return <Button variant="ghost" size="sm" onClick={load} data-testid="records-downloads-open"><Eye size={14} className="mr-1.5" />Voir les consultations</Button>;
  const names = Object.fromEntries((doc.versions || []).flatMap((v) => v.files.map((f) => [f.id, f.name])));
  return (
    <div className="space-y-2" data-testid="records-downloads">
      <h3 className="text-sm font-medium text-slate-900">Consultations des fichiers</h3>
      {rows === null && <Skeleton className="h-8 w-full" />}
      {rows?.length === 0 && <p className="text-xs text-slate-500">Personne n'a encore ouvert un fichier de ce document.</p>}
      {rows?.length > 0 && (
        <ul className="divide-y divide-slate-100 text-sm">
          {rows.map((r, i) => (
            <li key={`${r.file_id}-${r.at}-${i}`} className="flex flex-wrap justify-between gap-x-3 py-1.5">
              <span className="text-slate-900">{r.by}</span>
              <span className="text-slate-500 text-xs">{names[r.file_id] || "Fichier"} · {when(r.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RecordDocument() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const people = usePeople();
  const [doc, setDoc] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/records/documents/${id}`);
      setDoc(data);
      setPhase("ready");
    } catch (e) {
      setError(e.response?.status === 404 ? "Ce document n'existe pas ou vous n'y avez pas accès." : recordsError(e, "Impossible de charger le document."));
      setPhase("error");
    }
  }, [id]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);

  const done = (data) => { setDialog(null); if (data?.id) setDoc(data); else load(); };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-40 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="records-document-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{error}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" asChild><Link to="/documents">Retour au registre</Link></Button>
          <Button variant="outline" onClick={() => { setPhase("loading"); load(); }}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </div>
      </Card>
    );
  }

  const caps = doc.capabilities || {};
  // How the document was closed (abandoned, archived, terminated) and why, shown under the title and not only in the history.
  const closing = ["abandoned", "archived", "terminated"].includes(doc.status) ? [...doc.history].reverse().find((h) => ["abandon", "archive", "terminate"].includes(h.kind)) : null;
  const main = primaryAction(doc);
  const hint = datesHint(doc, todayIso());
  const open = (action) => setDialog(action);
  const secondary = [
    caps.edit && "edit", caps.add_version && main !== "add_version" && "add_version", caps.manage_access && "manage_access",
    caps.amend && "amend", caps.return && "return", caps.terminate && "terminate", caps.archive && "archive", caps.abandon && "abandon", caps.delete && "delete",
  ].filter(Boolean);
  const runAction = (action) => (action === "amend" ? navigate(`/documents/nouveau?parent=${doc.id}`) : open(action));
  const signature = doc.signature;

  return (
    <div className="space-y-4" data-testid="records-document-page">
      <div className="space-y-1">
        <Link to="/documents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Documents et contrats</Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="records-document-title">{doc.title}</h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <span className="tabular-nums">{doc.number}</span><span>· {doc.type_label}</span>
              {doc.amendment_number && <span>· avenant n° {doc.amendment_number}</span>}
              <StateBadge state={doc.state} /><ClassificationBadge value={doc.classification} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {main && (
              <Button onClick={() => open(main)} data-testid={`records-action-${main}`}>
                {main === "sign" && <FileSignature size={15} className="mr-1.5" />}{ACTION_LABELS[main]}
              </Button>
            )}
            {secondary.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Autres actions" data-testid="records-more"><MoreHorizontal size={16} /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" title="Actions sur le document">
                  {secondary.map((action, i) => (
                    <div key={action}>
                      {["return", "terminate"].includes(action) && i > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuItem onClick={() => runAction(action)} data-testid={`records-menu-${action}`}
                        className={["terminate", "archive", "abandon", "delete"].includes(action) ? "text-red-600" : ""}>
                        {action === "add_version" ? "Nouvelle version" : action === "delete" ? "Supprimer…" : ACTION_LABELS[action]}
                      </DropdownMenuItem>
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {closing && (
        <Card className="p-3 border-slate-300 bg-slate-50 text-sm text-slate-800" data-testid="records-closing">
          <span className="font-medium">{HISTORY_LABELS[closing.kind]} le {when(closing.created_at)}{closing.actor_name ? ` par ${closing.actor_name}` : ""}.</span>
          {historyDetail(closing) && <span className="block text-slate-700 break-words">{historyDetail(closing)}</span>}
        </Card>
      )}
      {caps.self_review && (
        <Card className="p-3 border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="records-self-review">
          Vous avez rédigé ou soumis cette version : une autre personne doit l'approuver.
        </Card>
      )}
      {hint && <Card className="p-3 border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="records-dates-hint">{hint}</Card>}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <div className="space-y-4">
          <Card className="p-4 sm:p-5 space-y-3" data-testid="records-versions">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold text-slate-900">Versions</h2>
              {caps.add_version && main !== "add_version" && <Button variant="outline" size="sm" onClick={() => open("add_version")}>Nouvelle version</Button>}
            </div>
            {doc.versions.length === 0 && (
              <p className="text-sm text-slate-500">Aucun fichier pour l'instant. Ajoutez-le pour pouvoir soumettre le document en revue.</p>
            )}
            <ol className="space-y-3">
              {doc.versions.map((v) => (
                <li key={v.id} className={`rounded-md border px-3 py-2.5 space-y-2 ${v.id === doc.current_version_id ? "border-jamora/40 bg-jamora/5" : "border-slate-200"}`}
                  data-testid={`records-version-${v.number}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="text-sm font-medium text-slate-900">
                      Version {v.number}{v.signed && <span className="ml-2 text-xs font-normal text-emerald-700">copie signée</span>}
                      {v.id === doc.current_version_id && <span className="ml-2 text-xs font-normal text-slate-500">en cours</span>}
                    </div>
                    <div className="text-xs text-slate-500">{v.author_name} · {when(v.created_at)}</div>
                  </div>
                  {v.note && <p className="text-sm text-slate-600">{v.note}</p>}
                  <FileLinks files={v.files} />
                </li>
              ))}
            </ol>
          </Card>

          {signature && (
            <Card className="p-4 sm:p-5" data-testid="records-signature">
              <h2 className="font-semibold text-slate-900 mb-2">Signature</h2>
              <dl>
                <Field label="Signé le">{formatDay(signature.signed_on)}</Field>
                <Field label="Mode">{SIGNATURE_MODES[signature.mode] || signature.mode}</Field>
                {signature.jamora_signatories?.length > 0 && <Field label="Pour Jamora">{signature.jamora_signatories.join(", ")}</Field>}
                {signature.counterpart_signatories?.length > 0 && <Field label="Autres parties">{signature.counterpart_signatories.join(", ")}</Field>}
              </dl>
            </Card>
          )}

          {(doc.parent || doc.amendments.length > 0 || caps.amend) && (
            <Card className="p-4 sm:p-5 space-y-2" data-testid="records-amendments">
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold text-slate-900">Avenants</h2>
                {caps.amend && <Button variant="outline" size="sm" onClick={() => runAction("amend")} data-testid="records-amend">Créer un avenant</Button>}
              </div>
              {doc.parent && (
                <p className="text-sm text-slate-600">Modifie <Link className="text-jamora hover:underline" to={`/documents/${doc.parent.id}`}>{doc.parent.number} — {doc.parent.title}</Link></p>
              )}
              {doc.amendments.length === 0 && !doc.parent && <p className="text-sm text-slate-500">Aucun avenant. Toute modification d'un document signé passe par un avenant.</p>}
              <ul className="divide-y divide-slate-100">
                {doc.amendments.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <Link to={`/documents/${a.id}`} className="text-sm text-slate-900 hover:underline">Avenant n° {a.amendment_number} · {a.title}</Link>
                    <StateBadge state={a.state} />
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <DocumentObligationsCard doc={doc} />

          <Card className="p-4 sm:p-5 space-y-3" data-testid="records-history">
            <h2 className="font-semibold text-slate-900">Historique</h2>
            <ol className="space-y-2">
              {[...doc.history].reverse().map((h, i) => (
                <li key={`${h.kind}-${h.created_at}-${i}`} className="text-sm">
                  <div className="flex flex-wrap justify-between gap-x-3">
                    <span className="text-slate-900">{HISTORY_LABELS[h.kind] || h.kind}</span>
                    <span className="text-xs text-slate-500">{h.actor_name} · {when(h.created_at)}</span>
                  </div>
                  {historyDetail(h) && <p className="text-xs text-slate-600 break-words">{historyDetail(h)}</p>}
                </li>
              ))}
            </ol>
            {caps.see_downloads && <Downloads doc={doc} />}
          </Card>
        </div>

        <aside className="space-y-4">
          <Card className="p-4 space-y-1" data-testid="records-details">
            <h2 className="font-semibold text-slate-900 mb-1">Détails</h2>
            <dl>
              <Field label="Responsable">{doc.responsible_name || "—"}</Field>
              <Field label="Date d'effet">{doc.effective_date ? formatDay(doc.effective_date) : "—"}</Field>
              <Field label="Expiration">{doc.expiry_date ? formatDay(doc.expiry_date) : "—"}</Field>
              {doc.notice_days != null && <Field label="Préavis">{doc.notice_days} jours{doc.notice_day ? ` (avant le ${formatDay(doc.notice_day)})` : ""}</Field>}
              <Field label="Reconduction">{doc.tacit_renewal ? "Tacite" : "Non"}</Field>
              {doc.terminated_on && <Field label="Résilié le">{formatDay(doc.terminated_on)}</Field>}
              {doc.retention_until && <Field label="Conserver jusqu'au">{formatDay(doc.retention_until)}</Field>}
              {doc.generated_from && (
                <Field label="Généré depuis">
                  <Link className="text-jamora hover:underline" to={`/documents/modeles/${doc.generated_from.template_id}`} data-testid="records-generated-from">
                    {doc.generated_from.template_name} (version {doc.generated_from.version_number})
                  </Link>
                </Field>
              )}
              {doc.generated_from?.clauses?.length > 0 && (
                <Field label="Clauses">
                  <span data-testid="records-generated-clauses">{doc.generated_from.clauses.map((c) => `${c.name} (version ${c.version_number})`).join(" · ")}</span>
                </Field>
              )}
              {doc.subscription && (
                <Field label="Abonnement">
                  {hasPermission(user, "billing:view") ? <Link className="text-jamora hover:underline" to={`/revenus/abonnements/${doc.subscription.id}`}>{doc.subscription.number}</Link> : doc.subscription.number}
                </Field>
              )}
            </dl>
          </Card>
          <Card className="p-4 space-y-2" data-testid="records-parties-card">
            <h2 className="font-semibold text-slate-900">Parties</h2>
            <ul className="space-y-1.5">
              {doc.parties.map((p) => (
                <li key={`${p.kind}-${p.ref || p.name}`} className="text-sm">
                  {p.kind === "customer" ? <Link className="text-slate-900 hover:underline" to={`/documents?party=${p.ref}`}>{p.name}</Link> : <span className="text-slate-900">{p.name}</span>}
                  <span className="text-xs text-slate-500"> · {p.kind === "organisation" ? ORGANISATION_ROLES[p.role] : PARTY_KIND_LABELS[p.kind]}</span>
                </li>
              ))}
            </ul>
          </Card>
          {doc.description && (
            <Card className="p-4 space-y-1"><h2 className="font-semibold text-slate-900">Description</h2><p className="text-sm text-slate-700 whitespace-pre-line break-words">{doc.description}</p></Card>
          )}
        </aside>
      </div>

      {dialog === "add_version" && <VersionDialog doc={doc} open onClose={() => setDialog(null)} onDone={done} />}
      {["submit", "approve"].includes(dialog) && <ConfirmDialog doc={doc} action={dialog} open onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "sign" && <SignDialog doc={doc} open onClose={() => setDialog(null)} onDone={done} />}
      {["return", "archive", "abandon"].includes(dialog) && <ReasonDialog doc={doc} action={dialog} open onClose={() => setDialog(null)} onDone={done} />}
      <DeleteWithReasonDialog
        target={dialog === "delete" ? doc : null} testId="delete-record" onClose={() => setDialog(null)} confirmLabel="Supprimer définitivement"
        title={`Supprimer le brouillon ${doc.number} ?`}
        description="Ce brouillon n'a jamais été soumis : il est supprimé pour de bon, avec son motif gardé dans l'historique. Les fichiers sont conservés. Un document déjà soumis s'abandonne à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/records/documents/${doc.id}`, { data: { reason } });
            toast.success("Brouillon supprimé");
            navigate("/documents");
          } catch (e) {
            setDialog(null);
            toast.error(errorMessage(e, "Impossible de supprimer ce brouillon"));
          }
        }}
      />
      {dialog === "terminate" && <TerminateDialog doc={doc} open onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "manage_access" && (
        <AccessDialog doc={doc} people={people} canConfidential={hasPermission(user, "records:view_confidential")} open onClose={() => setDialog(null)} onDone={done} />
      )}
      {dialog === "edit" && <EditDialog doc={doc} people={people} open onClose={() => setDialog(null)} onDone={done} />}
    </div>
  );
}
